import { z } from 'zod';

/**
 * PCO field-write events — the first worker domain in the catalog (ADR 0002).
 *
 * Motivating incident: `saveField` read `data_type` off a key the PCO client
 * doesn't produce, so every field routed to the plain-text handler. Dates,
 * checkboxes and booleans were written as raw strings for months. PCO accepted
 * most of them, so the jobs returned SUCCESS and nothing anywhere recorded that
 * the value was wrong — the only trace was a handful of `must be either 'true'
 * or 'false'` rows in JobExecution.
 *
 * The fix for that class of bug is not another error log: it's emitting which
 * handler actually ran for which data type, so a collapsed distribution (every
 * field arriving as `handler: 'text'`) is visible on a chart.
 */

/** The branch saveField dispatched to. `text` is the fallback. */
export const PCO_FIELD_HANDLERS = [
  'text',
  'boolean',
  'checkbox',
  'date',
  'number',
  'file',
  /**
   * Resolves the value against the field's FieldOption rows before writing.
   * Added 2026-08-10, when all 21 field_save_failed events in the prior 14
   * days were one church's select field arriving as handler:'text' daily —
   * select had no handler and fell through to `text`. (That church's failures
   * turned out to be the same-value duplicate PATCH, fixed separately via the
   * `already_current` recovery strategy; the handler fixes the routing and the
   * option-mismatch class the text path can't see.)
   */
  'select',
] as const;

export type PcoFieldHandler = (typeof PCO_FIELD_HANDLERS)[number];

export const PCO_FIELD_FAILURE_REASONS = [
  /** Our own pre-flight validation rejected the value (church misconfiguration). */
  'validation',
  /** The field definition no longer exists in PCO. */
  'field_missing',
  /** PCO reported a duplicate datum and the recovery path could not complete. */
  'duplicate_unrecovered',
  /** Any other non-2xx from PCO. */
  'pco_api',
  'unknown',
] as const;

export type PcoFieldFailureReason = (typeof PCO_FIELD_FAILURE_REASONS)[number];

/** How the duplicate-datum recovery resolved the conflict. */
export const PCO_DUPLICATE_RECOVERY_STRATEGIES = [
  /** PATCHed the existing datum in place (every non-upload data type). */
  'updated_in_place',
  /** Deleted then re-created so PCO re-runs the upload (`file` fields). */
  'recreated_upload',
  /** No datum was visible despite the 422 — retried the create once. */
  'retried_create',
  /**
   * The datum already held exactly the value being written, so nothing was
   * PATCHed. Added 2026-08-10: PCO 422s a same-value PATCH on a `select`
   * datum with the very duplicate error the recovery was invoked for, so a
   * recurring automation re-writing the same value failed daily as
   * duplicate_unrecovered (church 17f2b967…, all 21 field_save_failed events
   * Aug 5–9; every Rollbar stack ends in updateResource — onark-worker #950).
   */
  'already_current',
  /**
   * Deleted then re-created because the datum is a `select` and PCO refuses
   * value-PATCHes on select datums outright — production proved a
   * different-value PATCH 422s with the same duplicate error that triggered
   * the recovery (church 17f2b967…, job 1402: campus A → campus B), while
   * date PATCHes on the same person succeed. Shipped gated behind
   * PCO_SELECT_RECREATE_RECOVERY and ungated 2026-08-20 once production
   * emitted it (church 17f2b967…, three recoveries on 2026-08-19). Only
   * taken for a value the select handler verified against the field's
   * options.
   */
  'recreated_select',
] as const;

export type PcoDuplicateRecoveryStrategy =
  (typeof PCO_DUPLICATE_RECOVERY_STRATEGIES)[number];

/**
 * Which lookup located the existing datum before the recovery acted on it.
 *
 * The split is about whether we saw the person's real row ordering, because
 * that is what decides whether `matchOffset` means anything.
 */
export const PCO_DUPLICATE_LOOKUPS = [
  /**
   * PCO applied the server-side `where[field_definition_id]` filter, so we only
   * ever saw rows for this definition. No offset is knowable from that.
   */
  'filtered',
  /**
   * We read the person's unfiltered field data — either via the paged fallback,
   * or because PCO declined to apply the filter and handed back everything.
   * The offset is real in both cases.
   */
  'paged',
  /** Nothing was located — PCO reported a duplicate we could not see. */
  'none',
] as const;

export type PcoDuplicateLookup = (typeof PCO_DUPLICATE_LOOKUPS)[number];

// `dataType` is a free string, not an enum: it is whatever the church's PCO
// returns, and a value we don't recognise is itself the signal.
const dataType = z.string();

/**
 * Why an automation is considered permanently broken. Mirrors the
 * PERMANENT_FAILURE_STATUSES set the worker uses to skip BullMQ retries — these
 * are the failures no retry and no passage of time can fix.
 */
export const PCO_AUTOMATION_BREAKAGES = [
  /** The PCO workflow this automation targets no longer exists. */
  'workflow_missing',
  /** The token can see the workflow list but not write cards. */
  'workflow_forbidden',
  /** The automation has no workflow id saved at all. */
  'workflow_unset',
  /**
   * The PCO field definition this automation writes to no longer exists — or
   * exists but rejects the write as not-found, which PCO reports identically.
   *
   * Deliberately NOT split into missing-vs-inaccessible: PCO answers both with
   * a 404, so the worker cannot tell them apart at the point it throws. See the
   * auto-pause note in automationBreakagePolicy — that ambiguity is exactly why
   * this breakage warns but does not pause.
   */
  'field_missing',
  /** The church's integration is unusable (misconfigured or needs re-auth). */
  'integration_unusable',
] as const;

export type PcoAutomationBreakage = (typeof PCO_AUTOMATION_BREAKAGES)[number];

/**
 * Why a PCO person create was refused before it reached PCO.
 *
 * Every one of these exists because the create path could not previously tell
 * "PCO says this person does not exist" from "PCO did not answer". The client
 * library swallows HTTP failures at the lowest level — `searchByEmail` and
 * `searchByPhone` both `catch { return [] }` — so during an outage a search
 * returns ZERO ROWS and the matcher reports a clean no-match. Every layer above
 * then reads that as permission to create.
 *
 * That is the mechanism behind the four-year duplicate pattern: 2023-03
 * (duplicate Growth Track workflow cards), 2024-11 (a top-tier customer nearly
 * churned), 2025-08-28 (one guest imported over 3,000 times during a PCO API
 * outage — the customer discovered it, we did not), 2026-01 (still recurring
 * after being reported fixed).
 */
const PCO_PERSON_CREATE_BLOCKS = [
  /**
   * A direct, un-swallowed read of PCO's people endpoint failed, so the
   * preceding "no match found" carries no information. Retry later rather than
   * create — this is the branch that would have stopped the 3,000-profile
   * import at profile one.
   */
  'search_unavailable',
  /**
   * The church has created more people in the rolling window than any real
   * service could produce. The ceiling is deliberately far above legitimate
   * Sunday traffic: crossing it means a loop, not a crowd.
   */
  'church_rate_cap',
  /**
   * One guest (church + email) has been created repeatedly in the rolling
   * window. The sharpest guard for the reported incident, which was a SINGLE
   * person duplicated thousands of times, not many people duplicated once.
   */
  'guest_repeat_cap',
] as const;

/**
 * Fields shared by the three automation lifecycle events (broken / warned /
 * disabled). `churchName` is optional: absent when the church row is gone (or
 * the lookup failed), which must not block the alert itself.
 */
const automationEventFields = {
  automationId: z.string(),
  breakage: z.enum(PCO_AUTOMATION_BREAKAGES),
  churchId: z.string(),
  churchName: z.string().optional(),
  /** Consecutive failures with no success since — the size of the hole. */
  consecutiveFailures: z.number().int().nonnegative(),
  /** Days since the first failure in the current unbroken run. */
  daysBroken: z.number().int().nonnegative(),
};

/**
 * Where the PCO error translator was called from (#692). Carried on
 * `pco.error_unclassified` so an unknown message is attributable to a call
 * site without ever logging the raw text.
 */
export const PCO_ERROR_SITES = [
  'pcoClient.onRefreshFailure',
  'pcoClient.getPcoClient',
  'pcoClient.refreshRejected',
] as const;

export type PcoErrorSite = (typeof PCO_ERROR_SITES)[number];

export const pcoSchemas = {
  /**
   * The translator fell through to its default (retryable NetworkError)
   * because no known wording matched. Carries a stable hash of the
   * lower-cased message, never the message: it can hold PCO response bodies.
   */
  'pco.error_unclassified': z.object({
    churchId: z.string().optional(),
    errorClass: z.string(),
    messageHash: z.string(),
    messageLength: z.number().int().nonnegative(),
    site: z.enum(PCO_ERROR_SITES),
  }),
  /** A field value was accepted by PCO. */
  'pco.field_saved': z.object({
    churchId: z.string(),
    dataType,
    handler: z.enum(PCO_FIELD_HANDLERS),
  }),
  /** A field write failed. Every early-return branch emits this (ADR 0002). */
  'pco.field_save_failed': z.object({
    churchId: z.string(),
    dataType: dataType.optional(),
    handler: z.enum(PCO_FIELD_HANDLERS).optional(),
    reason: z.enum(PCO_FIELD_FAILURE_REASONS),
  }),
  /**
   * PCO rejected a create as a duplicate and we recovered. Positive
   * confirmation that the recovery works — without it, a working fix and a
   * silently-skipped one look identical (both are just an absence of errors).
   *
   * The lookup fields below exist because "the recovery ran" was not enough.
   * Two verification rounds (2026-08-03, 2026-08-10) could not decide whether
   * the *paginated* lookup — the entire point of the fix — had ever executed:
   * every recovery reported only `strategy`, and a datum found on page one
   * looks identical to one found on page four. `legacyPageWouldHaveFound` is
   * the field that settles it, so a single recovery is now self-proving.
   */
  'pco.field_duplicate_recovered': z.object({
    churchId: z.string(),
    dataType: dataType.optional(),
    /** Which lookup located the datum. */
    lookup: z.enum(PCO_DUPLICATE_LOOKUPS),
    /**
     * Whether the pre-fix lookup — ONE unfiltered page at PCO's default 25
     * rows — would have found this datum. `false` is the proof that the fix
     * was load-bearing for this write; `true` means the old code would have
     * recovered it too. Absent when the probe itself failed, which is not the
     * same as `true` — do not collapse the two when reading a chart.
     */
    legacyPageWouldHaveFound: z.boolean().optional(),
    /**
     * Zero-based index of the datum among the rows scanned. Only set for
     * `lookup: 'paged'` — on the filtered path the offset is a position within
     * PCO's filtered result, which says nothing about the person's real
     * ordering, and reporting it would invite exactly that misreading.
     */
    matchOffset: z.number().int().nonnegative().optional(),
    /** Pages of field data read to locate it. >1 means paging was required. */
    pagesFetched: z.number().int().nonnegative(),
    /** Rows read across those pages. */
    rowsScanned: z.number().int().nonnegative(),
    strategy: z.enum(PCO_DUPLICATE_RECOVERY_STRATEGIES),
  }),
  /**
   * An automation has been failing permanently and is dropping every guest that
   * reaches it. Emitted once per automation per sweep, NOT once per guest.
   *
   * Motivating incident: a deleted PCO workflow made one church's "Yes, Count Me
   * In!" membership automation fail 260 times over seven months. Every failure
   * was recorded correctly in JobExecution and alerted individually to Rollbar,
   * and neither surfaced the one fact that mattered — that a specific automation
   * had stopped working entirely. Per-guest signal at that volume reads as
   * background noise; the aggregate is the alert.
   */
  'pco.automation_broken': z.object({
    ...automationEventFields,
    /**
     * True when the failure run reached the edge of the scan window, so both
     * counts above are floors rather than totals — read them as "at least".
     *
     * Not cosmetic: on the first production run theChapel reported 89 days and
     * 187 failures, clipped by the 90-day lookback, when the real breakage was
     * 206 days and 260 failures. A reader would have dated it to May instead of
     * January. Publishing a clipped number as if it were the total is the same
     * quiet misinformation this event exists to remove.
     */
    truncated: z.boolean(),
  }),
  /**
   * The breakage-notice ladder's first stage acted: a church's owners were
   * emailed that one of their automations isn't working. Emitted at most once
   * per automation per breakage episode — the AutomationNotice claim row is
   * the dedupe; a fixed-or-recovered automation clears it so a NEW episode
   * warns again.
   */
  'pco.automation_warned': z.object({
    ...automationEventFields,
    /** Owner addresses the warning was accepted for. */
    notifiedEmails: z.number().int().nonnegative(),
  }),
  /**
   * The dead-workflow policy acted: an automation that kept failing with a
   * permanent breakage was auto-disabled and the church's owners were emailed.
   * Emitted at most once per automation per breakage episode — the
   * AutomationNotice row it writes is the dedupe, and an admin editing
   * the automation deletes that row (re-enabling it), so a NEW episode after a
   * failed fix emits again.
   *
   * `pco.automation_broken` keeps firing while something is broken;
   * this event marks the moment we stopped the silent retry loop for it.
   */
  'pco.automation_disabled': z.object({
    ...automationEventFields,
    /** Owner addresses the disable notice was accepted for. */
    notifiedEmails: z.number().int().nonnegative(),
  }),
  /**
   * A person was created in PCO. The positive counterpart to
   * `pco.person_create_blocked`, and the event a runaway shows up on FIRST:
   * duplicates are not errors, so a create loop emits no failures anywhere. Its
   * only signature is this event's rate.
   *
   * `createsInWindow` is the church's running count at the moment of the
   * create, so one event is enough to see a loop — a reader does not have to
   * aggregate to notice that a single create was the 400th this hour.
   */
  'pco.person_created': z.object({
    churchId: z.string(),
    /** Creates by this church in the rolling window, including this one. */
    createsInWindow: z.number().int().positive(),
    /** Creates for this exact guest identity in the window, including this one. */
    createsForGuestInWindow: z.number().int().positive(),
  }),
  /**
   * A create was refused by the guard. Each of these is a duplicate that did
   * NOT get made — the event exists so the fix is self-proving, in the sense of
   * `pco.field_duplicate_recovered`: without it, a guard that works and a guard
   * that never runs look identical.
   */
  'pco.person_create_blocked': z.object({
    churchId: z.string(),
    /** Count that tripped the cap. Absent for `search_unavailable`. */
    createsInWindow: z.number().int().nonnegative().optional(),
    guestId: z.string(),
    reason: z.enum(PCO_PERSON_CREATE_BLOCKS),
  }),
  /**
   * A workflow add the worker refused on purpose: the guest already went
   * through this workflow (a completed or removed card), so the re-entry
   * policy left them out. The run is recorded as FAILED but the Rollbar
   * reporter drops it, so this is the only signal outside the run row that
   * the block fired. Read its rate against `AUTO-PCO-PCOWorkflow` runs: a
   * climb with nothing else changing means Planning Center or the client
   * library changed what it blocks.
   */
  'pco.workflow_reentry_blocked': z.object({
    churchId: z.string(),
    guestId: z.string(),
    workflowId: z.string(),
  }),
  /**
   * A contact-info search matched a PCO person the guard judged to be someone
   * else, so the match was refused.
   *
   * Catalogued because the first version of this guard emitted NOTHING on
   * rejection, which made it impossible to tell in production whether it ever
   * fired — the same "a guard that works and a guard that never runs look
   * identical" problem `pco.person_create_blocked` exists to solve.
   *
   * Read it against `pco.person_created`: a rejection that is immediately
   * followed by a create for the same guest is the guard working as designed. A
   * rejection rate that climbs without creates following means it is refusing
   * matches it should be accepting, and churches are about to see duplicates.
   */
  'pco.person_match_rejected': z.object({
    churchId: z.string(),
    guestId: z.string(),
    /** Which identifier proved the contact info belongs to several people. */
    sharedBy: z.enum(['email', 'phone']),
    /** Distinct PCO people holding that identifier. Always >= 2. */
    holders: z.number().int().min(2),
    /** Where the rejected match came from. */
    source: z.enum(['search_strategy', 'name_fallback', 'final_search']),
  }),
  /**
   * A saved personId was thrown away because PCO answered a definitive 404 —
   * the person really was merged or deleted, the one case where re-searching is
   * correct.
   *
   * Catalogued because this branch USED to fire on any failure at all: a
   * timeout, a 500 or a 429 during an outage was read as "merged", the personId
   * was discarded, and the search-then-create path ran with nothing left to
   * stop it. A spike here now means real PCO merges; during an outage it should
   * stay flat while `pco.person_create_blocked` rises.
   */
  'pco.person_id_discarded': z.object({
    churchId: z.string(),
    guestId: z.string(),
    personId: z.string(),
    /** Where the discarded id came from. */
    source: z.enum([
      'pre_provided',
      'guest_record',
      'other_guest',
      'refreshed',
    ]),
  }),
};
